import type { Dossier, InventoryComponent, InventoryVulnerabilityWithComponent } from '@/api/client'

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'unknown']
const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Kritiek',
  high: 'Hoog',
  medium: 'Gemiddeld',
  low: 'Laag',
  unknown: 'Onbekend',
}

/**
 * Build the Claude Code prompt for fixing a platform's outdated/vulnerable
 * dependencies. Same conventions as generateSeoPrompt (@/lib/seoPrompt):
 * root-cause-first instruction, severity-grouped action items, a closing
 * verification step — kept consistent across every "Claude instructies"
 * panel in this app so the pattern stays recognisable.
 */
export function generateVulnerabilityPrompt(
  target: Dossier,
  components: InventoryComponent[],
  vulnerabilities: InventoryVulnerabilityWithComponent[],
): string {
  const lines: string[] = []

  const kevVulns = vulnerabilities.filter(v => v.kev)
  const outdated = components.filter(c => c.is_outdated)

  if (vulnerabilities.length === 0 && outdated.length === 0) {
    return `Geen openstaande kwetsbaarheden of verouderde dependencies voor ${target.name}. Niets te doen.`
  }

  lines.push(`Los de dependency-kwetsbaarheden op in het project "${target.name}"${target.github_repo ? ` (${target.github_repo})` : ''}.`)
  if (target.url) lines.push(`Productie-URL: ${target.url}`)
  lines.push('')

  if (kevVulns.length > 0) {
    lines.push(
      `⚡ ${kevVulns.length} van de bevindingen hieronder staan op de CISA KEV-lijst ` +
      `(actief misbruikt in het wild, niet theoretisch) — behandel die als absolute prioriteit.`
    )
    lines.push('')
  }

  lines.push(
    `Werk per dependency, niet per CVE: één versie-upgrade lost vaak meerdere CVE's ` +
    `tegelijk op. Controleer na elke upgrade of de testsuite en de build nog slagen ` +
    `voor je verdergaat naar de volgende — een dependency-bump die de app breekt is ` +
    `erger dan de kwetsbaarheid die je probeerde te fixen.`
  )
  lines.push(
    `Upgrade nooit blind naar de nieuwste major-versie als alleen een patch/minor ` +
    `nodig is om de kwetsbaarheid te dichten (kolom "Fix" hieronder) — een onnodige ` +
    `major-bump voegt breaking-change-risico toe zonder extra beveiligingswinst.`
  )
  lines.push('')

  let taskNum = 1

  for (const sev of SEVERITY_ORDER) {
    const sevVulns = vulnerabilities.filter(v => v.severity === sev)
    if (!sevVulns.length) continue

    // Group by component so multiple CVE's on the same dependency become one task.
    const byComponent = new Map<string, InventoryVulnerabilityWithComponent[]>()
    for (const v of sevVulns) {
      const key = `${v.component_name}@${v.component_version}`
      if (!byComponent.has(key)) byComponent.set(key, [])
      byComponent.get(key)!.push(v)
    }

    lines.push(`## ${SEVERITY_LABELS[sev]} (${sevVulns.length})`)
    lines.push('')
    byComponent.forEach((vulns, componentKey) => {
      const fixVersions = [...new Set(vulns.map(v => v.fixed_version).filter(Boolean))]
      lines.push(`${taskNum++}. **${componentKey}**${vulns.some(v => v.kev) ? ' ⚡ KEV' : ''}`)
      for (const v of vulns) {
        const epss = v.epss_score !== null ? ` · EPSS ${(v.epss_score * 100).toFixed(1)}%` : ''
        lines.push(`   - \`${v.vuln_id}\`${epss}${v.fixed_version ? ` — fix: ${v.fixed_version}` : ' — geen fix-versie bekend, evalueer mitigatie/vervanging'}`)
      }
      if (fixVersions.length === 1) {
        lines.push(`   → Upgrade naar ${fixVersions[0]}.`)
      } else if (fixVersions.length > 1) {
        lines.push(`   → Upgrade naar minstens ${fixVersions[fixVersions.length - 1]} (dekt alle bovenstaande CVE's).`)
      }
      lines.push('')
    })
  }

  const outdatedNoVulns = outdated.filter(c => c.vuln_count === 0)
  if (outdatedNoVulns.length > 0) {
    lines.push(`## Verouderd, geen bekende CVE (${outdatedNoVulns.length})`)
    lines.push('')
    lines.push('Geen kwetsbaarheid vandaag, maar hoe ouder de versie hoe groter de kans dat een toekomstige CVE je hier nog vindt:')
    for (const c of outdatedNoVulns) {
      lines.push(`${taskNum++}. **${c.name}** ${c.version} → ${c.latest_version ?? '?'}`)
    }
    lines.push('')
  }

  lines.push('---')
  lines.push(
    `Na alle upgrades: run de volledige testsuite, deploy naar staging als die bestaat, ` +
    `en verifieer handmatig het kritieke pad (login/hoofdflow) voor je naar productie pusht.`
  )

  return lines.join('\n')
}
